import { isDevRuntime } from '../constants/devControls';

const LAST_SHOWN_KEY = 'riftbound_support_modal_last_shown';
const CARD_VIEWED_KEY = 'riftbound_card_viewed';
const SEARCH_COUNT_KEY = 'riftbound_search_count';
const LAST_SEARCH_KEY = 'riftbound_last_counted_search';
/** Distinct engagement keys for non-search flows (deck save, collection adds, scans, …). */
const FEATURE_ENGAGEMENT_KEYS_KEY = 'riftbound_support_feature_keys';
const SESSION_SHOWN_KEY = 'riftbound_support_modal_shown_session';
const FORCE_SHOW_KEY = 'riftbound_support_modal_force_show';

const MIN_SEARCH_LENGTH = 3;
const COOLDOWN_DAYS = 7;

let openSupportModal: (() => void) | null = null;

/** Wired by ModalProvider so non-React code paths can open the support modal. */
export function registerSupportModalOpener(open: (() => void) | null): void {
  openSupportModal = open;
}

function hasCooldownPassed(): boolean {
  const lastShown = localStorage.getItem(LAST_SHOWN_KEY);
  if (!lastShown) return true;

  const lastShownDate = new Date(parseInt(lastShown, 10));
  const now = new Date();
  const daysSinceLastShown =
    (now.getTime() - lastShownDate.getTime()) / (1000 * 60 * 60 * 24);

  return daysSinceLastShown >= COOLDOWN_DAYS;
}

function hasShownThisSession(): boolean {
  return sessionStorage.getItem(SESSION_SHOWN_KEY) === 'true';
}

export function markSupportModalShown(): void {
  sessionStorage.setItem(SESSION_SHOWN_KEY, 'true');
  localStorage.setItem(LAST_SHOWN_KEY, Date.now().toString());
}

export function trackCardViewed(): void {
  sessionStorage.setItem(CARD_VIEWED_KEY, 'true');
}

function hasViewedCard(): boolean {
  return sessionStorage.getItem(CARD_VIEWED_KEY) === 'true';
}

export function getSearchCount(): number {
  const count = sessionStorage.getItem(SEARCH_COUNT_KEY);
  return count ? parseInt(count, 10) : 0;
}

function getFeatureEngagementKeys(): Set<string> {
  try {
    const raw = sessionStorage.getItem(FEATURE_ENGAGEMENT_KEYS_KEY);
    const arr = raw ? (JSON.parse(raw) as string[]) : [];
    return new Set(arr);
  } catch {
    return new Set();
  }
}

function persistFeatureEngagementKeys(keys: Set<string>): void {
  sessionStorage.setItem(FEATURE_ENGAGEMENT_KEYS_KEY, JSON.stringify([...keys]));
}

/** Count of distinct tracked feature engagements this session (not including search tally). */
export function getFeatureEngagementCount(): number {
  return getFeatureEngagementKeys().size;
}

/**
 * Tracks a qualifying non-search interaction (deck saved, scan committed, etc.).
 * Keys must be stable per logical event you want deduped (e.g. `collection:${cardId}` for first-time add).
 */
export function recordSupportFeatureEngagement(dedupeKey: string): void {
  const keys = getFeatureEngagementKeys();
  if (keys.has(dedupeKey)) return;
  keys.add(dedupeKey);
  persistFeatureEngagementKeys(keys);
  tryTriggerSupportModal();
}

function getLastCountedSearch(): string {
  return sessionStorage.getItem(LAST_SEARCH_KEY) || '';
}

function isDistinctSearch(newSearch: string, lastSearch: string): boolean {
  if (!lastSearch) return true;

  const newLower = newSearch.toLowerCase().trim();
  const lastLower = lastSearch.toLowerCase().trim();

  return (
    newLower !== lastLower &&
    !newLower.startsWith(lastLower) &&
    !lastLower.startsWith(newLower)
  );
}

export function trackSearch(searchTerm: string): boolean {
  const trimmed = searchTerm.trim();

  if (trimmed.length < MIN_SEARCH_LENGTH) {
    return false;
  }

  const lastSearch = getLastCountedSearch();

  if (!isDistinctSearch(trimmed, lastSearch)) {
    sessionStorage.setItem(LAST_SEARCH_KEY, trimmed);
    return false;
  }

  const newCount = getSearchCount() + 1;
  sessionStorage.setItem(SEARCH_COUNT_KEY, newCount.toString());
  sessionStorage.setItem(LAST_SEARCH_KEY, trimmed);

  return true;
}

export function shouldShowSupportModal(): boolean {
  if (sessionStorage.getItem(FORCE_SHOW_KEY) === 'true') {
    sessionStorage.removeItem(FORCE_SHOW_KEY);
    return true;
  }

  const totalEngagement = getSearchCount() + getFeatureEngagementCount();
  const hasEnoughEngagement = totalEngagement >= 2;
  // Search-only users must open a card; deck/collection/scan flows count as "value" without the modal.
  const hasReceivedValue =
    hasViewedCard() || getFeatureEngagementCount() >= 1;
  const cooldownPassed = hasCooldownPassed();
  const notShownThisSession = !hasShownThisSession();

  return hasEnoughEngagement && hasReceivedValue && cooldownPassed && notShownThisSession;
}

export function tryTriggerSupportModal(): void {
  if (shouldShowSupportModal()) {
    openSupportModal?.();
  }
}

export function devForceShowSupportModal(): void {
  if (!isDevRuntime()) return;
  sessionStorage.setItem(FORCE_SHOW_KEY, 'true');
  sessionStorage.removeItem(SESSION_SHOWN_KEY);
  openSupportModal?.();
}

export function devResetSupportModalTracking(): void {
  if (!isDevRuntime()) return;
  sessionStorage.removeItem(SEARCH_COUNT_KEY);
  sessionStorage.removeItem(LAST_SEARCH_KEY);
  sessionStorage.removeItem(FEATURE_ENGAGEMENT_KEYS_KEY);
  sessionStorage.removeItem(CARD_VIEWED_KEY);
  sessionStorage.removeItem(SESSION_SHOWN_KEY);
  localStorage.removeItem(LAST_SHOWN_KEY);
}

/** Read-only eligibility inputs for SupportModalDevPanel (does not consume force-show flag). */
export function devSupportModalSignalsSnapshot(): {
  searchCount: number;
  featureEngagementCount: number;
  totalEngagement: number;
  cardViewed: boolean;
  shownThisSession: boolean;
  cooldownPassed: boolean;
} {
  return {
    searchCount: getSearchCount(),
    featureEngagementCount: getFeatureEngagementCount(),
    totalEngagement: getSearchCount() + getFeatureEngagementCount(),
    cardViewed: hasViewedCard(),
    shownThisSession: hasShownThisSession(),
    cooldownPassed: hasCooldownPassed(),
  };
}
